import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  Plus,
  Trash2,
  RefreshCw,
  Settings as SettingsIcon,
  Mail,
  Globe,
  CheckCircle,
  AlertTriangle,
  Info,
  ExternalLink,
  Users,
  Star,
  Edit3,
  ShieldCheck,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

export const Settings: React.FC = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Modals
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const openConfirm = (title: string, message: string, onConfirm: () => void) =>
    setConfirmDialog({ open: true, title, message, onConfirm });
  const closeConfirm = () => setConfirmDialog(d => ({ ...d, open: false }));

  const [isSenderModalOpen, setIsSenderModalOpen] = useState(false);
  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // Forms states
  const [newSenderName, setNewSenderName] = useState('');
  const [newSenderEmail, setNewSenderEmail] = useState('');
  const [newDomain, setNewDomain] = useState('');
  
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'ADMIN' | 'USER'>('USER');
  
  // Selected domain details modal
  const [selectedDomain, setSelectedDomain] = useState<any | null>(null);

  // Edit Sender Account States
  const [isEditSenderModalOpen, setIsEditSenderModalOpen] = useState(false);
  const [editingSender, setEditingSender] = useState<any | null>(null);
  const [editSenderName, setEditSenderName] = useState('');
  const [editSenderSortOrder, setEditSenderSortOrder] = useState(0);
  const [editSenderIsPrimary, setEditSenderIsPrimary] = useState(false);
  const [editSenderProfileImage, setEditSenderProfileImage] = useState('');

  // Edit User States
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserRole, setEditUserRole] = useState<'ADMIN' | 'USER'>('USER');
  const [editUserPassword, setEditUserPassword] = useState('');

  // Fetch Settings (AWS Config, senders, domains)
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await apiClient.get('/settings');
      return res.data;
    }
  });

  // Feature flags
  const { data: featureFlags } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: async () => {
      const res = await apiClient.get('/settings/feature-flags');
      return res.data as { sesEmailValidationEnabled: boolean };
    }
  });

  const updateFeatureFlagsMutation = useMutation({
    mutationFn: async (flags: { sesEmailValidationEnabled: boolean }) => {
      const res = await apiClient.put('/settings/feature-flags', flags);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['featureFlags']);
    },
    onError: () => {
      showToast('Failed to update setting', 'error');
    }
  });

  // Add Sender
  const addSenderMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/settings/senders', {
        name: newSenderName,
        email: newSenderEmail
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      showToast('Sender email registered. SES verification initiated.', 'success');
      setIsSenderModalOpen(false);
      setNewSenderName('');
      setNewSenderEmail('');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to add sender identity';
      showToast(msg, 'error');
    }
  });

  // Verify Sender status
  const verifySenderMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/settings/senders/${id}/verify`);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['settings']);
      showToast(`Identity status updated: ${data.status}`, 'info');
    },
    onError: () => {
      showToast('Failed to check sender verification status', 'error');
    }
  });

  // Delete Sender
  const deleteSenderMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/settings/senders/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      showToast('Sender identity removed', 'success');
    },
    onError: () => {
      showToast('Failed to delete sender identity', 'error');
    }
  });

  // Update Sender (for editing properties like sorting order, profile image, name, or primary status)
  const updateSenderMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiClient.put(`/settings/senders/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      showToast('Sender account updated successfully', 'success');
      setIsEditSenderModalOpen(false);
      setEditingSender(null);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to update sender account';
      showToast(msg, 'error');
    }
  });

  const handleEditSenderClick = (sender: any) => {
    setEditingSender(sender);
    setEditSenderName(sender.name || '');
    setEditSenderSortOrder(sender.sortOrder || 0);
    setEditSenderIsPrimary(sender.isPrimary || false);
    setEditSenderProfileImage(sender.profileImage || '');
    setIsEditSenderModalOpen(true);
  };

  const handleTogglePrimary = (sender: any) => {
    updateSenderMutation.mutate({
      id: sender.id || sender._id,
      data: { isPrimary: !sender.isPrimary }
    });
  };

  // Add Domain
  const addDomainMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/settings/domains', {
        domain: newDomain
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['settings']);
      showToast('Domain identity registered. Create CNAME records to verify.', 'success');
      setIsDomainModalOpen(false);
      setNewDomain('');
      // Open details immediately
      setSelectedDomain(data);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to add domain identity';
      showToast(msg, 'error');
    }
  });

  // Verify Domain status
  const verifyDomainMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/settings/domains/${id}/verify`);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['settings']);
      showToast(`Domain verification status: ${data.status}`, 'info');
    },
    onError: () => {
      showToast('Failed to check domain verification status', 'error');
    }
  });

  // Delete Domain
  const deleteDomainMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/settings/domains/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      showToast('Domain identity removed', 'success');
    },
    onError: () => {
      showToast('Failed to delete domain identity', 'error');
    }
  });

  // Fetch Users List (Admin only)
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ['usersList'],
    queryFn: async () => {
      const res = await apiClient.get('/settings/users');
      return res.data;
    }
  });

  // Create User
  const createUserMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/settings/users', {
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['usersList']);
      showToast('User created successfully.', 'success');
      setIsUserModalOpen(false);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('USER');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to create user';
      showToast(msg, 'error');
    }
  });

  // Delete User
  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/settings/users/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['usersList']);
      showToast('User deleted successfully.', 'success');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to delete user';
      showToast(msg, 'error');
    }
  });

  // Update User properties (role, password reset)
  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiClient.put(`/settings/users/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['usersList']);
      showToast('User updated successfully.', 'success');
      setIsEditUserModalOpen(false);
      setEditingUser(null);
      setEditUserName('');
      setEditUserRole('USER');
      setEditUserPassword('');
    },
    onError: (err: any) => {
      const msg = err.response?.data?.error || 'Failed to update user';
      showToast(msg, 'error');
    }
  });

  const handleEditUserClick = (usr: any) => {
    setEditingUser(usr);
    setEditUserName(usr.name || '');
    setEditUserRole(usr.role || 'USER');
    setEditUserPassword('');
    setIsEditUserModalOpen(true);
  };

  const senders = data?.senders || [];
  const domains = data?.domains || [];
  const awsConfig = data?.awsConfig || {};
  const users = usersData || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">Platform Settings</h2>
        <p className="text-xs text-slate-550 dark:text-slate-400">Configure SES sender identities, DNS records, and sending limits.</p>
      </div>

      {isLoading ? (
        <div className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in">
            
            {/* Sender Accounts Card */}
            <div className="glass-panel rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <Mail size={16} className="text-primary-500" />
                  Sender Email Accounts
                </h3>
                <button
                  onClick={() => setIsSenderModalOpen(true)}
                  className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1"
                >
                  <Plus size={14} />
                  Add Email
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider pb-2">
                      <th className="pb-3 pr-4">Avatar</th>
                      <th className="pb-3 px-4">Display Name</th>
                      <th className="pb-3 px-4">Email</th>
                      <th className="pb-3 px-4">Primary</th>
                      <th className="pb-3 px-4">Order</th>
                      <th className="pb-3 px-4">Delivery Rate</th>
                      <th className="pb-3 px-4">Status</th>
                      <th className="pb-3 pl-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-slate-600 dark:text-slate-300">
                    {senders.map((s: any) => {
                      const isVerified = s.verificationStatus === 'VERIFIED';
                      
                      // Fallback colored avatar icon using sender name
                      const initial = s.name ? s.name.charAt(0).toUpperCase() : '?';
                      const colors = [
                        'bg-blue-500/10 text-blue-500 border-blue-500/20',
                        'bg-purple-500/10 text-purple-500 border-purple-500/20',
                        'bg-pink-500/10 text-pink-500 border-pink-500/20',
                        'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
                        'bg-teal-500/10 text-teal-500 border-teal-500/20',
                        'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                      ];
                      const colorIndex = (s.name || '').charCodeAt(0) % colors.length;
                      const avatarClass = colors[isNaN(colorIndex) ? 0 : colorIndex];

                      // Style for delivery rate
                      const rate = s.deliveryRate !== undefined ? s.deliveryRate : 100;
                      let rateColor = 'text-accent-emerald';
                      if (rate < 80) rateColor = 'text-accent-rose';
                      else if (rate < 95) rateColor = 'text-amber-400';

                      return (
                        <tr key={s.id || s._id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                          {/* Avatar Column */}
                          <td className="py-3 pr-4">
                            {s.profileImage ? (
                              <img
                                src={s.profileImage}
                                alt={s.name}
                                className="w-8 h-8 rounded-full object-cover border border-slate-200 dark:border-white/10"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full border flex items-center justify-center font-bold text-xs ${avatarClass}`}>
                                {initial}
                              </div>
                            )}
                          </td>
                          {/* Display Name */}
                          <td className="py-3 px-4 font-medium text-slate-800 dark:text-white">{s.name}</td>
                          {/* Email */}
                          <td className="py-3 px-4 font-mono text-[11px]">{s.email}</td>
                          {/* Primary */}
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleTogglePrimary(s)}
                              disabled={updateSenderMutation.isLoading}
                              className={`p-1 rounded hover:bg-slate-100 dark:hover:bg-white/5 transition-colors ${
                                s.isPrimary ? 'text-amber-400' : 'text-slate-400 hover:text-amber-400'
                              }`}
                              title={s.isPrimary ? 'Primary Sender' : 'Set as Primary'}
                            >
                              <Star size={15} fill={s.isPrimary ? 'currentColor' : 'none'} />
                            </button>
                          </td>
                          {/* Sort Order */}
                          <td className="py-3 px-4">
                            <span className="bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-350 px-2 py-0.5 rounded font-mono text-[10px]">
                              {s.sortOrder || 0}
                            </span>
                          </td>
                          {/* Delivery Rate */}
                          <td className="py-3 px-4 font-bold font-mono text-xs select-none">
                            <span className={rateColor}>{rate.toFixed(2)}%</span>
                          </td>
                          {/* Status */}
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full font-semibold border text-[10px] ${
                              isVerified 
                                ? 'bg-emerald-500/10 text-accent-emerald border-emerald-500/20' 
                                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            }`}>
                              {s.verificationStatus}
                            </span>
                          </td>
                          {/* Actions */}
                          <td className="py-3 pl-4 text-right">
                            <div className="flex justify-end items-center gap-1">
                              <button
                                onClick={() => handleEditSenderClick(s)}
                                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                title="Edit Sender Account"
                              >
                                <Edit3 size={13} />
                              </button>
                              <button
                                onClick={() => verifySenderMutation.mutate(s.id || s._id)}
                                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                title="Check status"
                              >
                                <RefreshCw size={13} />
                              </button>
                              <button
                                onClick={() => openConfirm(
                                  'Delete Sender Identity',
                                  `Remove "${s.emailAddress || s.email}"? This cannot be undone.`,
                                  () => deleteSenderMutation.mutate(s.id || s._id)
                                )}
                                className="text-slate-500 dark:text-slate-400 hover:text-accent-rose p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {senders.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-6 text-slate-500">
                          No sender emails registered.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Verified Domains Card */}
            <div className="glass-panel rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <Globe size={16} className="text-primary-500" />
                  Sender Domains
                </h3>
                <button
                  onClick={() => setIsDomainModalOpen(true)}
                  className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1"
                >
                  <Plus size={14} />
                  Add Domain
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider pb-2">
                      <th className="pb-3 pr-4">Domain</th>
                      <th className="pb-3 px-4">Status</th>
                      <th className="pb-3 px-4">DKIM Records</th>
                      <th className="pb-3 pl-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-slate-600 dark:text-slate-300">
                    {domains.map((d: any) => {
                      const isVerified = d.status === 'VERIFIED';
                      return (
                        <tr key={d.id || d._id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                          <td className="py-3 pr-4 font-medium text-slate-800 dark:text-white">{d.domain}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full font-semibold border text-[10px] ${
                              isVerified 
                                ? 'bg-emerald-500/10 text-accent-emerald border-emerald-500/20' 
                                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            }`}>
                              {d.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setSelectedDomain(d)}
                              className="text-primary-500 hover:text-primary-600 font-medium underline flex items-center gap-0.5"
                            >
                              Show CNAME DNS
                              <ExternalLink size={10} />
                            </button>
                          </td>
                          <td className="py-3 pl-4 text-right">
                            <div className="flex justify-end items-center gap-1.5">
                              <button
                                onClick={() => verifyDomainMutation.mutate(d.id || d._id)}
                                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                title="Check status"
                              >
                                <RefreshCw size={13} />
                              </button>
                              <button
                                onClick={() => openConfirm(
                                  'Delete Domain Identity',
                                  `Remove domain "${d.domain || d.emailAddress}"? This cannot be undone.`,
                                  () => deleteDomainMutation.mutate(d.id || d._id)
                                )}
                                className="text-slate-500 dark:text-slate-400 hover:text-accent-rose p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {domains.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-6 text-slate-500">
                          No sender domains registered.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* User Management Card */}
            <div className="glass-panel rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <Users size={16} className="text-primary-500" />
                  User Accounts
                </h3>
                <button
                  onClick={() => setIsUserModalOpen(true)}
                  className="btn-secondary py-1.5 px-3 text-xs flex items-center gap-1"
                >
                  <Plus size={14} />
                  Create User
                </button>
              </div>

              {isUsersLoading ? (
                <div className="py-6 text-center">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider pb-2">
                        <th className="pb-3 pr-4">Name</th>
                        <th className="pb-3 px-4">Email</th>
                        <th className="pb-3 px-4">Role</th>
                        <th className="pb-3 pl-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-slate-600 dark:text-slate-300">
                      {users.map((u: any) => {
                        const isSelf = u.id === user?.id || u._id === user?.id;
                        return (
                          <tr key={u.id || u._id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                            <td className="py-3 pr-4 font-medium text-slate-800 dark:text-white">{u.name}</td>
                            <td className="py-3 px-4">{u.email}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded-full font-semibold border text-[10px] ${
                                u.role === 'ADMIN' 
                                  ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' 
                                  : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                              }`}>
                                {u.role}
                              </span>
                            </td>
                            <td className="py-3 pl-4 text-right">
                              <div className="flex justify-end items-center gap-1">
                                <button
                                  onClick={() => handleEditUserClick(u)}
                                  className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                  title="Edit User Settings"
                                >
                                  <Edit3 size={13} />
                                </button>
                                {!isSelf && (
                                  <button
                                    onClick={() => openConfirm(
                                      'Delete User',
                                      `Remove user "${u.name}"? They will lose access immediately.`,
                                      () => deleteUserMutation.mutate(u.id || u._id)
                                    )}
                                    className="text-slate-500 dark:text-slate-400 hover:text-accent-rose p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
                                    title="Delete User"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {users.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center py-6 text-slate-500">
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Full-width SES Configuration Details */}
            <div className="glass-panel rounded-2xl p-6 space-y-6">
              <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <SettingsIcon size={16} className="text-primary-500" />
                SES Configuration
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Status banner */}
                <div className={`p-4 rounded-xl border flex gap-3 text-xs leading-relaxed lg:col-span-1 ${
                  awsConfig.useMockSes 
                    ? 'bg-blue-500/10 border-blue-500/20 text-blue-800 dark:text-blue-300' 
                    : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-800 dark:text-emerald-300'
                }`}>
                  {awsConfig.useMockSes ? (
                    <>
                      <Info size={18} className="text-blue-550 dark:text-blue-400 shrink-0" />
                      <div>
                        <strong className="text-slate-900 dark:text-white block mb-0.5">Mock Driver Mode Active</strong>
                        The platform is running in simulation mode. Emails will not actually be dispatched via AWS, making local verification safe and cost-free.
                      </div>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} className="text-accent-emerald shrink-0" />
                      <div>
                        <strong className="text-slate-900 dark:text-white block mb-0.5">Live Amazon SES Connected</strong>
                        Outgoing connections will route directly through Amazon SES Client endpoints.
                      </div>
                    </>
                  )}
                </div>

                {/* Details grid */}
                <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                  <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl flex flex-col justify-between">
                    <span className="text-slate-500 dark:text-slate-400 font-semibold mb-1">AWS Region</span>
                    <span className="font-bold text-slate-800 dark:text-white font-mono">{awsConfig.region}</span>
                  </div>
                  <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl flex flex-col justify-between">
                    <span className="text-slate-500 dark:text-slate-400 font-semibold mb-1">SES Config Set</span>
                    <span className="font-bold text-slate-805 dark:text-white font-mono">{awsConfig.sesConfigurationSet || 'None'}</span>
                  </div>
                  <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl flex flex-col justify-between">
                    <span className="text-slate-500 dark:text-slate-400 font-semibold mb-1">Max Send Rate</span>
                    <span className="font-bold text-slate-805 dark:text-white font-mono">{awsConfig.emailsPerSecond} / sec</span>
                  </div>
                  <div className="bg-slate-50 dark:bg-white/5 p-4 rounded-xl flex flex-col justify-between">
                    <span className="text-slate-500 dark:text-slate-400 font-semibold mb-1">Daily Limit</span>
                    <span className="font-bold text-slate-850 dark:text-white font-mono">{(awsConfig.emailsPerMinute || 0) * 1440} / day</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Validation Settings */}
            <div className="glass-panel rounded-2xl p-6 space-y-5">
              <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <ShieldCheck size={16} className="text-primary-500" />
                Email Deliverability Validation
              </h3>

              <div className="flex items-start justify-between gap-6">
                <div className="space-y-1 flex-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                    SES Email Validation
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                      PAID
                    </span>
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed max-w-xl">
                    When enabled, deliverability checks run AWS SES Email Validation after DNS — verifying that the mailbox actually exists, detecting disposable addresses, and flagging role accounts (info@, support@, etc.). More accurate than DNS alone. AWS charges per-email validated; check{' '}
                    <a
                      href="https://aws.amazon.com/ses/pricing/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:underline inline-flex items-center gap-0.5"
                    >
                      SES pricing <ExternalLink size={10} />
                    </a>{' '}
                    before enabling on large lists.
                  </p>
                  <div className="pt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    {[
                      { label: 'Bad domain (no MX)', both: true },
                      { label: 'Mailbox doesn\'t exist', ses: true },
                      { label: 'Disposable address', ses: true },
                      { label: 'SES suppression list', both: true },
                    ].map(item => (
                      <div key={item.label} className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                        {item.both || (item.ses && featureFlags?.sesEmailValidationEnabled) ? (
                          <CheckCircle size={13} className="text-emerald-500 shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 dark:border-slate-600 shrink-0" />
                        )}
                        {item.label}
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => updateFeatureFlagsMutation.mutate({
                    sesEmailValidationEnabled: !featureFlags?.sesEmailValidationEnabled
                  })}
                  disabled={updateFeatureFlagsMutation.isLoading}
                  className="shrink-0 flex flex-col items-center gap-1.5"
                  title={featureFlags?.sesEmailValidationEnabled ? 'Disable SES Email Validation' : 'Enable SES Email Validation'}
                >
                  {featureFlags?.sesEmailValidationEnabled ? (
                    <ToggleRight size={44} className="text-primary-500" />
                  ) : (
                    <ToggleLeft size={44} className="text-slate-400 dark:text-slate-600" />
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${
                    featureFlags?.sesEmailValidationEnabled ? 'text-primary-500' : 'text-slate-400'
                  }`}>
                    {featureFlags?.sesEmailValidationEnabled ? 'ON' : 'OFF'}
                  </span>
                </button>
              </div>
            </div>

          </div>
      )}

      {/* Add Sender Modal */}
      <Modal
        isOpen={isSenderModalOpen}
        onClose={() => setIsSenderModalOpen(false)}
        title="Add Sender Email"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Display Name</label>
            <input
              type="text"
              placeholder="ReachOut Team"
              value={newSenderName}
              onChange={(e) => setNewSenderName(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</label>
            <input
              type="email"
              placeholder="offers@promo.company.com"
              value={newSenderEmail}
              onChange={(e) => setNewSenderEmail(e.target.value)}
              className="form-input w-full"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => setIsSenderModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!newSenderName || !newSenderEmail || addSenderMutation.isLoading}
              onClick={() => addSenderMutation.mutate()}
              className="btn-primary"
            >
              Verify Sender
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Sender Modal */}
      <Modal
        isOpen={isEditSenderModalOpen}
        onClose={() => {
          setIsEditSenderModalOpen(false);
          setEditingSender(null);
        }}
        title="Edit Sender Account"
      >
        <div className="space-y-4">
          {/* Profile Image upload and preview */}
          <div className="flex flex-col items-center gap-3 mb-4">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider self-start">Profile Image</label>
            <div className="flex items-center gap-4 w-full">
              {editSenderProfileImage ? (
                <img
                  src={editSenderProfileImage}
                  alt="Profile Preview"
                  className="w-16 h-16 rounded-full object-cover border border-slate-200 dark:border-white/10"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center text-slate-450 text-2xl font-bold uppercase">
                  {editSenderName ? editSenderName.charAt(0) : '?'}
                </div>
              )}
              <div className="flex flex-col gap-1.5 flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > 500 * 1024) {
                        showToast('File size must be under 500KB', 'error');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setEditSenderProfileImage(reader.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="text-xs text-slate-500 file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary-500/10 file:text-primary-500 hover:file:bg-primary-500/20 cursor-pointer"
                />
                {editSenderProfileImage && (
                  <button
                    type="button"
                    onClick={() => setEditSenderProfileImage('')}
                    className="text-left text-xs text-accent-rose hover:underline"
                  >
                    Remove Image
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Display Name</label>
            <input
              type="text"
              value={editSenderName}
              onChange={(e) => setEditSenderName(e.target.value)}
              className="form-input w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Sort Order</label>
              <input
                type="number"
                value={editSenderSortOrder}
                onChange={(e) => setEditSenderSortOrder(parseInt(e.target.value, 10) || 0)}
                className="form-input w-full"
              />
            </div>

            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="editSenderIsPrimary"
                checked={editSenderIsPrimary}
                onChange={(e) => setEditSenderIsPrimary(e.target.checked)}
                className="rounded border-slate-300 dark:border-white/10 text-primary-500 focus:ring-primary-500 bg-transparent h-4 w-4"
              />
              <label htmlFor="editSenderIsPrimary" className="text-xs font-semibold text-slate-700 dark:text-slate-350 cursor-pointer">
                Primary Sender Account
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => {
                setIsEditSenderModalOpen(false);
                setEditingSender(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!editSenderName || updateSenderMutation.isLoading}
              onClick={() => {
                if (editingSender) {
                  updateSenderMutation.mutate({
                    id: editingSender.id || editingSender._id,
                    data: {
                      name: editSenderName,
                      isPrimary: editSenderIsPrimary,
                      sortOrder: editSenderSortOrder,
                      profileImage: editSenderProfileImage || null
                    }
                  });
                }
              }}
              className="btn-primary"
            >
              {updateSenderMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Domain Modal */}
      <Modal
        isOpen={isDomainModalOpen}
        onClose={() => setIsDomainModalOpen(false)}
        title="Add Verified Domain"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Domain Name</label>
            <input
              type="text"
              placeholder="promo.company.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              className="form-input w-full"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => setIsDomainModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!newDomain || addDomainMutation.isLoading}
              onClick={() => addDomainMutation.mutate()}
              className="btn-primary"
            >
              Verify Domain
            </button>
          </div>
        </div>
      </Modal>

      {/* Domain Verification Details Modal */}
      <Modal
        isOpen={!!selectedDomain}
        onClose={() => setSelectedDomain(null)}
        title={`DNS Settings: ${selectedDomain?.domain}`}
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-start gap-3">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <div className="text-xs leading-relaxed text-slate-605 dark:text-slate-300">
              <span className="font-semibold text-slate-800 dark:text-white block mb-0.5">DNS Action Required:</span>
              To complete DKIM and verification for <strong>{selectedDomain?.domain}</strong>, add the following CNAME records to your DNS provider (e.g. Route53, GoDaddy, Cloudflare).
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-805 dark:text-white uppercase tracking-wider">DKIM CNAME Records (Easy DKIM)</h4>
            <div className="space-y-2 max-w-full">
              {selectedDomain?.dkimTokens?.map((token: string, idx: number) => {
                const name = `${token}._domainkey.${selectedDomain.domain}.`;
                const value = `${token}.dkim.amazonses.com.`;
                return (
                  <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-white/5 rounded-lg text-[10px] space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">CNAME Name</span>
                      <code className="text-slate-705 dark:text-slate-300 font-mono select-all">{name}</code>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">CNAME Value</span>
                      <code className="text-slate-705 dark:text-slate-300 font-mono select-all">{value}</code>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedDomain?.txtRecord && (
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-slate-805 dark:text-white uppercase tracking-wider">Verification TXT Record (Alternative)</h4>
              <div className="p-3 bg-slate-50 dark:bg-slate-950/40 border border-slate-200 dark:border-white/5 rounded-lg text-[10px] space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">TXT Name</span>
                  <code className="text-slate-705 dark:text-slate-300 font-mono">_amazonses.{selectedDomain.domain}</code>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">TXT Value</span>
                  <code className="text-slate-705 dark:text-slate-300 font-mono select-all">{selectedDomain.txtRecord}</code>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={() => setSelectedDomain(null)}
              className="btn-secondary"
            >
              Close Details
            </button>
          </div>
        </div>
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        title="Create New User"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Full Name</label>
            <input
              type="text"
              placeholder="Alice Smith"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</label>
            <input
              type="email"
              placeholder="alice@company.com"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Role</label>
            <select
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value as 'ADMIN' | 'USER')}
              className="form-input w-full bg-white dark:bg-slate-900"
            >
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => setIsUserModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!newUserName || !newUserEmail || !newUserPassword || createUserMutation.isLoading}
              onClick={() => createUserMutation.mutate()}
              className="btn-primary"
            >
              Create User
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setEditingUser(null);
        }}
        title="Edit User Account"
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Full Name</label>
            <input
              type="text"
              value={editUserName}
              onChange={(e) => setEditUserName(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</label>
            <input
              type="email"
              disabled
              value={editingUser?.email || ''}
              className="form-input w-full bg-slate-100 dark:bg-white/5 text-slate-550 cursor-not-allowed"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Reset Password (leave blank to keep current)</label>
            <input
              type="password"
              placeholder="••••••••"
              value={editUserPassword}
              onChange={(e) => setEditUserPassword(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Role</label>
            <select
              value={editUserRole}
              disabled={editingUser?.id === user?.id || editingUser?._id === user?.id}
              onChange={(e) => setEditUserRole(e.target.value as 'ADMIN' | 'USER')}
              className={`form-input w-full bg-white dark:bg-slate-900 ${
                (editingUser?.id === user?.id || editingUser?._id === user?.id) ? 'bg-slate-100 dark:bg-white/5 text-slate-550 cursor-not-allowed' : ''
              }`}
            >
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
            {(editingUser?.id === user?.id || editingUser?._id === user?.id) && (
              <span className="text-[10px] text-amber-500 italic mt-0.5">
                You cannot modify your own role.
              </span>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
            <button
              type="button"
              onClick={() => {
                setIsEditUserModalOpen(false);
                setEditingUser(null);
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!editUserName || updateUserMutation.isLoading}
              onClick={() => {
                if (editingUser) {
                  const data: any = {
                    name: editUserName,
                    role: editUserRole
                  };
                  if (editUserPassword) {
                    data.password = editUserPassword;
                  }
                  updateUserMutation.mutate({
                    id: editingUser.id || editingUser._id,
                    data
                  });
                }
              }}
              className="btn-primary"
            >
              {updateUserMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        onConfirm={() => { confirmDialog.onConfirm(); closeConfirm(); }}
        onCancel={closeConfirm}
      />
    </div>
  );
};
export default Settings;
